import parse from 'html-react-parser';
import { isEmpty } from 'radash';
import { useController } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import {
    DataModelAttributeBaseClass,
    DataModelAttributeTypeName,
} from '@/types/DataModel';

import { AngularJSTemplateRenderer } from '../AngularJSTemplateRenderer';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useValidationsStore, Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';
import { isAngularJSTemplate } from '../utils/angularjs-detection';

interface CustomTemplateProps extends EditorComponent {
    // String specific props go here
    isDimensional?: boolean;
    className?: string;
    template: any;
}

const CustomTemplate = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    hidden = false,
    readonly = false,
    hideLabel = false,
    isDimensional = false,
    beforeAttributeControl,
    className,
    handleUrlChange,
    labelClassName,
    template
}: CustomTemplateProps) => {

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
    });

    if (hidden) {
        return null;
    }

    const isStringType =
        attribute.typeName === DataModelAttributeTypeName.String ||
        attribute.baseClass === DataModelAttributeBaseClass.String;
    const layoutClassName = isStringType ? undefined : 'tw-w-full';

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={false}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!readonly && !isDimensional}
            labelClassName={labelClassName}
            labelVerticalAlign="top"
            layoutClassName={layoutClassName}
        >
            { isAngularJSTemplate(template) ?
                <AngularJSTemplateRenderer
                    template={template}
                    attribute={attribute}
                    fieldPath={fieldPath}
                    scope={{
                        field: field,
                        readonly: readonly,
                        hasErrors: hasErrors,
                        hasWarnings: hasWarnings,
                        handleUrlChange: handleUrlChange
                    }}
                    className={`tw-pb-1 ${className ?? ''}`}
                /> :
                parse(template)}
        </Layout>
    );
};

export { CustomTemplate };
